import { apiClient, buildQueryString } from "@/shared/lib/api/client";
import type {
  BoardTask,
  Task,
  TaskBoard,
  TaskCreate,
  TaskUpdate,
  TaskProgress,
  TaskInjectRequest,
  TaskInjectResponse,
  TaskEntriesResponse,
  TaskSkipRequest,
  TaskSkipResponse,
} from "../types";

const BASE_PATH = "/api/tasks";

// ===================
// CRUD Operations
// ===================

export async function getTasks(params?: {
  skip?: number;
  limit?: number;
  status?: string;
}): Promise<{ items: Task[]; total: number }> {
  const query = buildQueryString({
    skip: params?.skip ?? 0,
    limit: params?.limit ?? 50,
    status: params?.status,
  });
  return apiClient<{ items: Task[]; total: number }>(`${BASE_PATH}${query}`);
}

export async function getTask(id: string): Promise<Task> {
  return apiClient<Task>(`${BASE_PATH}/${id}`);
}

export async function createTask(data: TaskCreate): Promise<Task> {
  return apiClient<Task>(BASE_PATH, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateTask(id: string, data: TaskUpdate): Promise<Task> {
  return apiClient<Task>(`${BASE_PATH}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteTask(id: string): Promise<void> {
  return apiClient<void>(`${BASE_PATH}/${id}`, {
    method: "DELETE",
  });
}

// ===================
// Task Queries
// ===================

export async function getAvailableTasks(): Promise<Task[]> {
  return apiClient<Task[]>(`${BASE_PATH}/available`);
}

export async function getOverdueTasks(): Promise<Task[]> {
  return apiClient<Task[]>(`${BASE_PATH}/overdue`);
}

export async function getInquiryTasks(status?: string): Promise<Task[]> {
  const query = status ? buildQueryString({ status }) : "";
  return apiClient<Task[]>(`${BASE_PATH}/inquiry${query}`);
}

export async function getAgentSpawnedTasks(limit?: number): Promise<Task[]> {
  const query = limit ? buildQueryString({ limit }) : "";
  return apiClient<Task[]>(`${BASE_PATH}/agent-spawned${query}`);
}

export async function getTasksForInputType(inputTypeId: string): Promise<Task[]> {
  return apiClient<Task[]>(`${BASE_PATH}/for-input-type/${inputTypeId}`);
}

export async function getTaskEntries(taskId: string): Promise<TaskEntriesResponse> {
  return apiClient<TaskEntriesResponse>(`${BASE_PATH}/${taskId}/entries`);
}

// ===================
// Task Actions
// ===================

export async function startTask(id: string): Promise<Task> {
  return apiClient<Task>(`${BASE_PATH}/${id}/start`, {
    method: "POST",
  });
}

export async function completeTask(
  id: string,
  completionEntryId?: string
): Promise<Task> {
  const query = completionEntryId
    ? buildQueryString({ completion_entry_id: completionEntryId })
    : "";
  return apiClient<Task>(`${BASE_PATH}/${id}/complete${query}`, {
    method: "POST",
  });
}

export async function dismissTask(id: string): Promise<Task> {
  return apiClient<Task>(`${BASE_PATH}/${id}/dismiss`, {
    method: "POST",
  });
}

export async function skipTask(
  id: string,
  data: TaskSkipRequest = {}
): Promise<TaskSkipResponse> {
  return apiClient<TaskSkipResponse>(`${BASE_PATH}/${id}/skip`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function addTaskProgress(
  id: string,
  data: TaskProgress
): Promise<Task> {
  return apiClient<Task>(`${BASE_PATH}/${id}/progress`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ===================
// The board — the one list every client reads
// ===================

export async function getTaskBoard(): Promise<TaskBoard> {
  return apiClient<TaskBoard>(`${BASE_PATH}/list`);
}

/** Server-side snooze: no reminder fires again until `until`. */
export async function snoozeTask(id: string, until: Date): Promise<BoardTask> {
  return apiClient<BoardTask>(`${BASE_PATH}/${id}/snooze`, {
    method: "POST",
    body: JSON.stringify({ until: until.toISOString() }),
  });
}

/** Moves a to-do to a new time, or to someday with `null`. */
export async function rescheduleTask(id: string, dueAt: Date | null): Promise<BoardTask> {
  return apiClient<BoardTask>(`${BASE_PATH}/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ due_at: dueAt ? dueAt.toISOString() : null }),
  });
}

export async function injectTaskData(
  id: string,
  data: TaskInjectRequest
): Promise<TaskInjectResponse> {
  return apiClient<TaskInjectResponse>(`${BASE_PATH}/${id}/inject`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
